import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useDeletePhdSupervisorMutation,
  useGetPhdSupervisorsQuery,
} from '../../../../../redux/services/phd.jsx';
import { AppBarCMS } from '../../../../basic';
import { CollapseBlock, QuestionModal, Spinner } from '../../../../common';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import PhdSupervisorsTable from './PhdSupervisorsTable.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';

const SUPERVISOR_TYPES = ['all', 'fri', 'external'];

const PhdSupervisorsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const typeParam = searchParams.get('type');
  const [type, setType] = useState(
    SUPERVISOR_TYPES.includes(typeParam) ? typeParam : 'all',
  );
  const [rowToDelete, setRowToDelete] = useState(null);
  const { deleteSuccess, deleteFailed } = useToastMessages();
  const pageTitle = t('Školitelia');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  useEffect(() => {
    if (SUPERVISOR_TYPES.includes(typeParam) && typeParam !== type) {
      setType(typeParam);
    }
  }, [type, typeParam]);

  const {
    data: supervisors,
    isLoading,
    error: loadError,
    refetch,
  } = useGetPhdSupervisorsQuery();
  const [deleteSupervisor] = useDeletePhdSupervisorMutation();

  const supervisorRows = useMemo(() => {
    const supervisorRows = Array.isArray(supervisors)
      ? supervisors
      : supervisors?.data || [];
    return supervisorRows.map((supervisor) => ({
      ...supervisor,
      type_label: supervisor.type === 'fri' ? t('FRI') : t('Externý'),
      workplace:
        supervisor.type === 'fri'
          ? supervisor.department_name || t('FRI UNIZA')
          : supervisor.external_university,
    }));
  }, [supervisors, t]);

  const rows =
    type === 'all'
      ? supervisorRows
      : supervisorRows.filter((row) => row.type === type);

  const changeType = (nextType) => {
    setType(nextType);
    setSearchParams(nextType === 'all' ? {} : { type: nextType });
  };

  const canEditRow = () =>
    Object.prototype.hasOwnProperty.call(
      user?.permissions || {},
      'phd-supervisors.update',
    );
  const canDeleteRow = () =>
    Object.prototype.hasOwnProperty.call(
      user?.permissions || {},
      'phd-supervisors.destroy',
    );

  const editRow = (row) => {
    navigate(`${routes.cmsPhdSupervisorsEdit}/${row.id}`);
  };

  const deleteRow = async () => {
    if (!rowToDelete) {
      return;
    }

    try {
      await deleteSupervisor(rowToDelete.id).unwrap();
      refetch();
      deleteSuccess();
      setRowToDelete(null);
    } catch (error) {
      deleteFailed(error);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Témy dizertačných prác')}
        createText={t('Pridať školiteľa')}
        createUrl={routes.cmsPhdSupervisorsCreate}
        permissionsBasic="phd-supervisors"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje evidencia školiteľov?')}
          description={t(
            'Evidencia spája interných a externých PhD školiteľov. Aktívny školiteľ sa po uložení zobrazí vo výberoch pre témy a plány.',
          )}
        />
        <TableFilter
          title={t('Typ školiteľa')}
          storageKey="PhD-supervisor-type"
          value={type}
          setValue={changeType}
          options={[
            { value: 'all', label: t('Všetci'), count: supervisorRows.length },
            {
              value: 'fri',
              label: 'FRI',
              count: supervisorRows.filter((row) => row.type === 'fri').length,
            },
            {
              value: 'external',
              label: t('Externí'),
              count: supervisorRows.filter((row) => row.type === 'external')
                .length,
            },
          ]}
        />
        <CollapseBlock>
          <PhdSupervisorsTable
            rows={rows}
            canEditRow={canEditRow}
            canDeleteRow={canDeleteRow}
            onEdit={editRow}
            onDelete={setRowToDelete}
          />
        </CollapseBlock>
      </div>
      <QuestionModal
        chosenPositive={deleteRow}
        question={t(
          'Naozaj chcete vymazať túto položku? Položka bude trvalo odstránená. Operáciu nie je možné vrátiť späť!',
        )}
        visible={rowToDelete !== null}
        yes={t('Áno')}
        toggle={() => setRowToDelete(null)}
        headerTitle={t('Vymazať položku')}
      />
    </>
  );
};

export default PhdSupervisorsPage;
